import { Square2StackIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";
import React, { useState } from "react";
import Modal from "../../../components/common/Modal";
import Button from "../../../components/form/Button";
import LoadingSpinner from "../../../components/loaders/LoadingSpinner";
import { copyAsRichText } from "../../../utils";
import ArticleHTML from "./ArticleHTML";

const ArticleTranscriptModal = ({
  articleTitle,
  articleUrl,
  onRequestClose,
}: {
  articleTitle: string;
  articleUrl: string;
  onRequestClose: () => void;
}) => {
  const [copyStatus, setCopyStatus] = useState<"" | "copying" | "copied">("");
  const renderIcon = () => {
    switch (copyStatus) {
      case "copying":
        return <LoadingSpinner size="16px" />;
      case "copied":
        return <CheckIcon className="w-4 h-4 text-green-600" />;
      default:
        return <Square2StackIcon className="w-4 h-4" />;
    }
  };
  return (
    <Modal
      onRequestClose={onRequestClose}
      maxWidth={1280}
      width={1280}
      isOpen={true}
      shouldCloseOnOverlayClick={true}
      disableScroll={true}
    >
      <div className="px-4 md:px-12 py-2 h-[calc(100vh-1rem)] relative flex flex-col">
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 my-6 relative">
          <div className="text-xl md:text-2xl font-bold pr-8 md:pr-0">
            {articleTitle}
          </div>
          <div className="flex items-center gap-2">
            <div className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
              Source Transcript
            </div>
            <div className="md:hidden">
              <Button
                tooltip="Copy transcript"
                paddingZero
                variant={copyStatus === "" ? "transparent" : "disabled"}
                className="group h-auto p-2 min-h-6 min-w-6 text-sm text-gray-400 cursor-pointer bg-gray-100 hover:bg-gray-100 hover:text-gray-500 rounded-full flex items-center justify-center"
                onClick={async () => {
                  if (copyStatus === "") {
                    setCopyStatus("copying");
                    copyAsRichText(articleUrl)
                      .then(() => {
                        setCopyStatus("copied");
                        setTimeout(() => setCopyStatus(""), 1000);
                      })
                      .catch(() => {
                        setCopyStatus("");
                      });
                  }
                }}
              >
                {renderIcon()}
              </Button>
            </div>
          </div>
        </div>
        <div className="hidden md:block absolute top-4 right-4 md:right-12">
          <Button
            paddingZero
            variant={copyStatus === "" ? "transparent" : "disabled"}
            className="group h-auto p-2 min-h-6 min-w-6 text-sm text-gray-400 cursor-pointer bg-gray-100 hover:bg-gray-100 hover:text-gray-500 rounded-full flex items-center justify-center"
            onClick={async () => {
              if (copyStatus === "") {
                setCopyStatus("copying");
                copyAsRichText(articleUrl)
                  .then(() => {
                    setCopyStatus("copied");
                    setTimeout(() => setCopyStatus(""), 1000);
                  })
                  .catch(() => {
                    setCopyStatus("");
                  });
              }
            }}
          >
            <div className="flex items-center w-0 h-4 group-hover:w-[108px] transition-all ease-linear duration-200 overflow-hidden">
              <div className="hidden opacity-0 group-hover:flex group-hover:opacity-100 text-xs flex-nowrap whitespace-nowrap mx-1">
                Copy to clipboard
              </div>
            </div>
            {renderIcon()}
          </Button>
        </div>
        <div className="border rounded-lg px-3 md:px-6 py-4 bg-white shadow-sm text-md leading-8 flex-1 overflow-y-scroll hover:[&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-transparent [&::-webkit-scrollbar-track]:bg-transparent transition-colors duration-200">
          <ArticleHTML articleUrl={articleUrl} />
        </div>
      </div>
    </Modal>
  );
};

export default ArticleTranscriptModal;
